import React, { useEffect } from "react";
import {
  FaCloud,
  FaAndroid,
  FaMobileAlt,
  FaCogs,
  FaLaptopCode,
  FaCode,
  FaBug,
  FaBrain,
  FaLayerGroup,
  FaInfinity,
  FaChartLine,
  FaShieldAlt, // ⭐ Added for Cybersecurity
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Services.css";

import AOS from "aos";
import "aos/dist/aos.css";

const Services = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const servicesData = [
    {
      id: 1,
      icon: <FaCloud />,
      title: "Cloud Solutions",
      shortDesc: "Reliable and scalable cloud infrastructure.",
      longDesc: `
        Our Cloud Solutions service is designed to help businesses of all sizes transition smoothly to the cloud.
        We offer in-depth assessments to identify the best cloud platforms for your unique needs.
        Whether you choose AWS, Microsoft Azure, or Google Cloud, we ensure optimal architecture and deployment.
        Our team focuses on scalability, so your infrastructure grows seamlessly with your business.
        Security is our top priority, with multi-layer encryption, intrusion detection, and access control.
        We also implement cost-optimization strategies to reduce unnecessary spending.
        Disaster recovery solutions ensure business continuity in case of outages.
        Performance monitoring tools give you real-time insights into your systems.
        We handle cloud migration without downtime, ensuring minimal disruption.
        Our experts set up automated backups for critical data.
        We configure load balancing for high-traffic scenarios.
        Custom integrations allow your cloud to work with existing business tools.
        Our DevOps services ensure continuous delivery pipelines are cloud-ready.
        With our managed services, you focus on business while we handle maintenance.
        In short, we make your move to the cloud smooth, secure, and future-ready.
      `,
    },
    {
      id: 2,
      icon: <FaAndroid />,
      title: "Android Development",
      shortDesc: "Powerful native Android applications.",
      longDesc: `
        Our Android Development service delivers high-performance, feature-rich mobile apps.
        We start by understanding your business goals and target audience.
        Using native development with Java and Kotlin, we ensure maximum performance and reliability.
        Our designs follow Google's Material Design principles for a consistent user experience.
        We integrate APIs and third-party services to extend functionality.
        Offline mode capabilities allow users to access essential features without the internet.
        Our apps are optimized for speed, ensuring smooth navigation and transitions.
        Security measures include encrypted data storage and secure authentication.
        We perform extensive testing across multiple devices and OS versions.
        Push notifications and in-app messaging keep your users engaged.
        We support app store publishing, ensuring compliance with Google Play policies.
        Analytics integration helps track user behavior and app performance.
        We provide regular updates to keep your app compatible with new OS releases.
        Our maintenance packages ensure your app stays bug-free.
        Ultimately, we create Android apps that not only work flawlessly but also delight users.
      `,
    },
    {
      id: 3,
      icon: <FaMobileAlt />,
      title: "Mobile App Development",
      shortDesc: "Cross-platform mobile app solutions.",
      longDesc: `
        Our Mobile App Development service covers both Android and iOS platforms.
        We specialize in cross-platform frameworks like Flutter and React Native.
        This approach allows you to launch apps faster while keeping development costs low.
        We design responsive layouts that adapt perfectly to different screen sizes.
        Our apps are optimized for performance and battery efficiency.
        We integrate social media logins for a seamless onboarding process.
        Payment gateway integration ensures secure transactions.
        Location-based services allow personalized user experiences.
        We implement data synchronization across devices in real-time.
        Our team performs rigorous testing on both emulators and real devices.
        We ensure accessibility compliance for a broader audience.
        Regular updates keep your app secure and feature-rich.
        We also offer post-launch support and analytics tracking.
        With our mobile solutions, your brand reaches customers anywhere, anytime.
        The end result: a sleek, functional app that users love to engage with.
      `,
    },
    {
      id: 4,
      icon: <FaCogs />,
      title: "SaaS Development",
      shortDesc: "Custom SaaS product development.",
      longDesc: `
        Our SaaS Development service turns your software idea into a fully functional cloud product.
        We design multi-tenant architectures to serve multiple customers efficiently.
        Our platforms scale automatically to handle increased demand.
        We ensure data security with encryption and secure authentication systems.
        Subscription management is integrated for flexible billing models.
        We use APIs for seamless integration with third-party services.
        Our UX/UI team ensures an intuitive and professional interface.
        We add analytics dashboards for actionable insights.
        Automated backups prevent data loss.
        Our DevOps pipelines ensure smooth deployments and updates.
        We implement AI and machine learning features where needed.
        The product is tested for performance, load, and security.
        We offer migration support from legacy systems.
        Our maintenance services keep your SaaS product running 24/7.
        In the end, you get a robust, scalable, and profitable SaaS solution.
      `,
    },
    {
      id: 5,
      icon: <FaLaptopCode />,
      title: "UI/UX Design",
      shortDesc: "Beautiful and functional designs.",
      longDesc: `
        Our UI/UX Design service focuses on creating user-friendly and visually appealing digital experiences.
        We begin with user research to understand your audience's needs.
        Wireframing and prototyping bring concepts to life before development.
        Our designs follow best practices in usability and accessibility.
        Color schemes and typography are chosen to reflect your brand identity.
        We ensure consistency across all screens and platforms.
        Interactive prototypes allow for feedback before finalization.
        We design responsive layouts for mobile, tablet, and desktop.
        Animations and micro-interactions enhance engagement.
        Our team uses tools like Figma and Adobe XD for precision.
        We collaborate with developers for pixel-perfect implementation.
        Usability testing helps refine the user journey.
        We optimize designs for fast load times and smooth transitions.
        Accessibility features make your product usable by everyone.
        The result: a design that delights users and drives conversions.
      `,
    },
    {
      id: 6,
      icon: <FaCode />,
      title: "Web Development",
      shortDesc: "Modern, responsive, and scalable websites.",
      longDesc: `
        Our Web Development service builds fast, secure, and scalable websites.
        We work with modern stacks like React, Node.js, and Next.js.
        Websites are fully responsive for mobile, tablet, and desktop.
        We optimize load times for better SEO and user retention.
        Custom CMS development gives you control over your content.
        E-commerce solutions come with secure payment integrations.
        Our developers ensure W3C-compliant, clean code.
        We integrate analytics tools for performance tracking.
        Websites are tested for cross-browser compatibility.
        We implement advanced security measures to prevent cyber threats.
        Our sites support multilingual content for global reach.
        We also offer API integrations for enhanced functionality.
        Ongoing support ensures your site stays up-to-date.
        We follow SEO best practices for higher search rankings.
        The outcome: a future-proof website that grows with your business.
      `,
    },
    {
      id: 7,
      icon: <FaBug />,
      title: "Software Testing",
      shortDesc: "Ensure quality with rigorous testing.",
      longDesc: `
        Our Software Testing service guarantees your software meets the highest quality standards.
        We provide both manual and automated testing solutions.
        Functional testing ensures features work as intended.
        Performance testing checks speed and scalability.
        Security testing identifies and fixes vulnerabilities.
        Usability testing ensures a smooth user experience.
        We use automation tools like Selenium and Cypress.
        Continuous testing is integrated into the CI/CD pipeline.
        Cross-platform testing covers multiple devices and browsers.
        Regression testing ensures updates don’t break existing features.
        We generate detailed reports for transparency.
        Load testing simulates high-traffic scenarios.
        We also perform API and database testing.
        Post-release monitoring ensures quick bug resolution.
        With us, your software launches bug-free and user-ready.
      `,
    },
    {
      id: 8,
      icon: <FaBrain />,
      title: "AI / ML Solutions",
      shortDesc: "Smart automation with Artificial Intelligence.",
      longDesc: `
        Our AI and Machine Learning services help businesses leverage data-driven insights and intelligent automation.
        We develop custom models for prediction, classification, and recommendation systems.
        Natural Language Processing (NLP) enables advanced chatbots, text analysis, and sentiment tracking.
        Computer Vision solutions process images and videos for real-world insights.
        We integrate AI into existing business workflows for smarter decision-making.
        Data preprocessing and cleaning ensure high-quality model performance.
        We use tools like TensorFlow, PyTorch, and Scikit-learn for precision.
        Predictive analytics identifies trends and improves forecasting accuracy.
        AI-powered automation enhances productivity and reduces manual effort.
        Our MLOps pipelines ensure continuous improvement and retraining of models.
        With strong data privacy and ethical AI practices, your business stays future-ready.
        In short, we turn your data into intelligence, and intelligence into action.
      `,
    },
    {
      id: 9,
      icon: <FaLayerGroup />,
      title: "Full Stack Development (Java, Python, MERN)",
      shortDesc: "Comprehensive end-to-end web development.",
      longDesc: `
        Our Full Stack Development service covers front-end, back-end, and database development.
        We work with popular stacks like MERN (MongoDB, Express, React, Node.js), Java Spring Boot with Microservices, and Python Django.
        We also implement Kubernetes and Docker for containerized deployments and scalability.
        From architecture design to deployment, we handle every aspect of your application.
        Our front-end team ensures modern, responsive, and accessible interfaces.
        Back-end APIs are built for scalability, performance, and security.
        We use RESTful and GraphQL APIs for flexible data handling.
        Database solutions include MySQL, PostgreSQL, MongoDB, and Firebase.
        We ensure authentication, authorization, and data encryption for user safety.
        Our CI/CD pipelines automate testing and deployment for faster releases.
        We integrate cloud hosting solutions for smooth scalability.
        Our team follows agile methodologies for transparent progress.
        Post-deployment support ensures ongoing updates and maintenance.
        With us, you get a full-stack solution that’s robust, efficient, and ready to grow with your business.
      `,
    },
    {
      id: 10,
      icon: <FaInfinity />,
      title: "DevOps Services",
      shortDesc: "Seamless CI/CD pipelines and cloud automation.",
      longDesc: `
        Our DevOps Services streamline your software delivery process through automation, monitoring, and integration.
        We design CI/CD pipelines to ensure faster and more reliable deployments.
        Infrastructure as Code (IaC) with Terraform or AWS CloudFormation simplifies environment setup.
        Containerization with Docker ensures consistency across development and production.
        Kubernetes orchestration automates deployment, scaling, and management of applications.
        We integrate Jenkins, GitHub Actions, or GitLab CI for continuous delivery.
        Monitoring tools like Prometheus and Grafana provide real-time insights.
        Automated testing ensures quality at every stage of deployment.
        We help teams adopt Agile and DevOps culture for better collaboration.
        Security practices (DevSecOps) are embedded into every pipeline.
        Our experts optimize cloud costs and infrastructure performance.
        Whether it’s AWS, Azure, or GCP, we configure scalable cloud environments.
        We offer 24/7 support and maintenance for smooth operations.
        The result: faster releases, fewer errors, and continuous innovation.
      `,
    },
    {
      id: 11,
      icon: <FaChartLine />,
      title: "Data Analytics & Visualization",
      shortDesc: "Turn raw data into actionable insights.",
      longDesc: `
        Our Data Analytics & Visualization services empower businesses to make smarter, data-driven decisions.
        We collect, clean, and transform raw data from multiple sources into structured, meaningful insights.
        Our experts build interactive dashboards using Power BI, Tableau, and Google Data Studio.
        Predictive analytics identifies patterns and forecasts trends to guide business strategy.
        We create KPI-driven reports that highlight business performance and growth opportunities.
        Integration with AI and ML models provides deeper analytical intelligence.
        Real-time data visualization ensures timely decisions and operational awareness.
        Data security and compliance are maintained through encryption and access control.
        With our analytics expertise, we help you see the full story behind your data — enabling better planning, forecasting, and innovation.
      `,
    },

    // ⭐ NEW CYBERSECURITY SERVICE ADDED — NOTHING ELSE MODIFIED ⭐
    {
      id: 12,
      icon: <FaShieldAlt />,
      title: "Cybersecurity Services",
      shortDesc: "Advanced security solutions for complete protection.",
      longDesc: `
        Our Cybersecurity Services safeguard your business against modern digital threats.
        We provide end-to-end protection including vulnerability assessments, penetration testing,
        and secure architecture design.

        Network security solutions include firewalls, intrusion detection systems, and traffic monitoring.
        Application security ensures your software is safe from OWASP Top 10 vulnerabilities.
        We implement secure authentication, encryption, and API protection.

        Our team monitors systems in real-time to detect and respond to threats quickly.
        Cloud security ensures your AWS, Azure, or GCP environments remain compliant and protected.
        We help businesses meet global standards like ISO 27001, GDPR, HIPAA, and PCI-DSS.

        With proactive threat intelligence, incident response, and continuous monitoring,
        we ensure your infrastructure, applications, and data stay secure at all times.
      `,
    },
  ];

  const handleReadMore = (service) => {
    navigate(`/services/${service.id}`, {
      state: {
        title: service.title,
        longDesc: service.longDesc,
      },
    });
  };

  return (
    <section className="services-section" data-aos="fade-up">
      <h2 className="section-title" data-aos="fade-down">
        Our Services
      </h2>
      <span className="underline" data-aos="fade-right"></span>

      <div className="services-grid">
        {servicesData.map((service, index) => (
          <div
            className="service-card"
            key={service.id}
            data-aos="zoom-in"
            data-aos-delay={index * 100}
          >
            <div className="service-icon">{service.icon}</div>
            <h3>{service.title}</h3>
            <p>{service.shortDesc}</p>
            <button
              className="read-more-btn"
              onClick={() => handleReadMore(service)}
            >
              Read More
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Services;
